// The persona's role screen under the Advisory Cases stage (polish.P28b, #715).
// Locked comp: `.impeccable/mocks/decision/advisory-stages.html`, family A —
// one full-width card, not a second page: the four KPI tiles are a four-cell
// fact row in the card head (figure over comparison, split by hairlines), the
// persona's working list sits on the left and the persona's secondary panels
// are the ink pill tabs on the right, each tab label carrying its headline.
// The persona picker still picks the screen; the six-unit cap and "Show all N
// cases" survive. Every derivation lives in `RoleScreen.transform.ts`.

import { useState } from 'react';
import { ChevronRight, Inbox } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  EmptyState,
  HelpAnchor,
  Pill,
  SurfacePagePanel,
  SurfacePageTabs,
} from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import type { Case, RecommendedAction } from '@/versions/v1/schema/case';
import type { ScenarioId } from '@/versions/v1/schema/scenario';
import { SCENARIOS } from '@/versions/v1/shared/perturbations';
import { TrendChart } from '@/versions/v1/modules/_viz';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { APPROVAL_GATE_SEAT } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { FindingCard } from './FindingCard';
import { GATE_PRIMARY_BUTTON } from './gate-ui';
import {
  STAGE_INK_LINK,
  STAGE_LABEL_CAPS,
  STAGE_ROW_META,
  STAGE_ROW_BUTTON,
  STAGE_ROW_TITLE,
  STAGE_ROWS,
  statePillClass,
} from './stage-body-ui';
import { allActionsForCase, caseById, recentlyClosedCaption } from './Scene.transform';
import {
  PRIMARY_LIST_UNIT_CAP,
  primaryListUnits,
  roleScreenRecoveryChart,
  roleScreenTabs,
  roleScreenZeroTouchChart,
  visiblePrimaryListItems,
  type RoleScreenKpi,
  type RoleScreenPrimaryItem,
  type RoleScreenSecondaryPanel,
  type RoleScreenViewModel,
} from './RoleScreen.transform';

const PRIMARY_LIST_CAP = PRIMARY_LIST_UNIT_CAP;

/** A hairline-divided row inside a tab body. */
const TAB_ROW = 'px-4 py-2';

/** The left list scrolls under its sticky caps head (comp 430 px). */
const PRIMARY_SCROLL = 'max-h-[30rem] overflow-auto';
/** The tab body scrolls under the tab row (comp 390 px). */
const TAB_SCROLL = 'max-h-[27rem] overflow-auto';

type RoleScreenProps = {
  model: RoleScreenViewModel;
  cases: readonly Case[];
  actions: readonly RecommendedAction[];
  projectPath: string;
  sessionState: SessionState;
  activeScenarios?: readonly ScenarioId[];
  onToggleScenario?: (id: ScenarioId) => void;
  showScenarioToggle?: boolean;
  showScopeMeta?: boolean;
  canApproveFmea: boolean;
  onOpenCase: (caseId: string) => void;
  onApproveReview?: (reviewId: string) => void;
  onRejectReview?: (reviewId: string, reason: string) => void;
};

// ---------------------------------------------------------------------------
// Fact row
// ---------------------------------------------------------------------------

function FactCell({ kpi, onOpenCase }: { kpi: RoleScreenKpi; onOpenCase: (caseId: string) => void }) {
  const body = (
    <>
      <span className={cn(STAGE_LABEL_CAPS, 'truncate')}>{kpi.label}</span>
      <span className="text-xl font-semibold leading-tight tracking-[-0.01em] tabular-nums text-ink-primary" data-role-fact-value>
        {kpi.value}
      </span>
      <span className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs leading-snug text-muted">
        <span>{kpi.caption}</span>
        {kpi.provenance ? (
          <ProvenanceChip
            text={kpi.provenanceChip ?? kpi.provenance}
            title={kpi.provenanceChip ? kpi.provenance : undefined}
            compact
          />
        ) : null}
      </span>
    </>
  );
  const cell = 'flex min-w-0 flex-col gap-0.5 px-4 py-2.5';
  const linked = kpi.linkCaseIds?.[0];
  return linked ? (
    <button
      type="button"
      data-role-fact={kpi.label}
      onClick={() => onOpenCase(linked)}
      className={cn(cell, STAGE_ROW_BUTTON)}
    >
      {body}
    </button>
  ) : (
    <div data-role-fact={kpi.label} className={cell}>{body}</div>
  );
}

// ---------------------------------------------------------------------------
// Tab bodies (the persona's secondary panels, drawn flat inside the card)
// ---------------------------------------------------------------------------

function TabBody({
  panel,
  cases,
  actions,
  sessionState,
  onOpenCase,
  canApproveFmea,
  onApproveReview,
}: {
  panel: RoleScreenSecondaryPanel;
  cases: readonly Case[];
  actions: readonly RecommendedAction[];
  sessionState: SessionState;
  onOpenCase: (caseId: string) => void;
  canApproveFmea: boolean;
  onApproveReview?: (reviewId: string) => void;
}) {
  switch (panel.kind) {
    case 'loops':
      return (
        <ul className={cn(STAGE_ROWS, 'text-sm')}>
          {panel.loops.map((loop) => (
            <li key={loop.tag} className={TAB_ROW}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-mono text-xs font-semibold text-ink-primary">{loop.tag}</p>
                <Pill tone={loop.tone} className={statePillClass(loop.tone)}>{loop.modeLabel}</Pill>
              </div>
              <p className="mt-0.5 text-xs text-ink-secondary">{loop.name}</p>
              <p className="mt-0.5 text-xs text-muted">{loop.note}</p>
            </li>
          ))}
        </ul>
      );
    case 'patterns':
      return (
        <div>
          <ul className={cn(STAGE_ROWS, 'text-sm')}>
            {panel.rows.map((row) => (
              <li key={row.label} className={cn(TAB_ROW, 'py-2.5')}>
                <div className="flex flex-wrap items-center gap-2">
                  <p className={STAGE_ROW_TITLE}>{row.label}</p>
                  <Pill tone="warning">{row.pattern}</Pill>
                </div>
                {row.causes.length > 0 ? (
                  <ul className="mt-1 space-y-0.5 text-xs leading-relaxed text-ink-secondary">
                    {row.causes.map((cause) => (
                      <li key={cause.ruleId} title={cause.ruleId}>
                        {cause.prose}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
          {panel.stableLine ? (
            <p className="border-t border-[var(--hairline)] px-4 py-2.5 text-xs leading-relaxed text-muted">{panel.stableLine}</p>
          ) : null}
        </div>
      );
    case 'fmea-queue':
      return (
        <ul className={cn(STAGE_ROWS, 'text-sm')}>
          {panel.rows.map((row) => (
            <li key={row.reviewId} className={TAB_ROW}>
              <p className="font-mono text-[10px] text-muted">
                {row.reviewId}
                {row.caseCode ? ` · ${row.caseCode}` : ''}
              </p>
              <p className={cn(STAGE_ROW_TITLE, 'mt-0.5')}>{row.summary}</p>
              <p className="mt-0.5 text-xs text-muted">
                {row.rulingStateLabel}
                <span title={row.occurrenceTitle} className="tabular-nums"> · O · {row.occurrence}</span>
              </p>
            </li>
          ))}
        </ul>
      );
    case 'ranked-excursions':
      return <RankedExcursionsBody panel={panel} />;
    case 'recovery-trend': {
      const chart = roleScreenRecoveryChart(sessionState);
      return (
        <div className="px-4 py-3" data-role-tab-chart="recovery">
          {panel.provenance ? (
            <div className="mb-1 flex justify-end">
              <ProvenanceChip
                text={panel.provenanceChip ?? panel.provenance}
                title={panel.provenanceChip ? panel.provenance : undefined}
                compact
              />
            </div>
          ) : null}
          <TrendChart categories={chart.categories} series={chart.series} references={chart.references} height={160} />
        </div>
      );
    }
    case 'work-order':
      return (
        <div className="space-y-1.5 px-4 py-3 text-sm">
          <div className="flex items-center justify-between gap-2">
            <p className="font-mono text-xs text-muted">{panel.wo}</p>
            <ProvenanceChip text={panel.provenanceChip} title={panel.provenance} compact />
          </div>
          <p className={STAGE_ROW_TITLE}>{panel.asset}</p>
          <p className="text-muted">{panel.description}</p>
          {panel.caseCode ? <p className="text-xs text-muted">Linked case {panel.caseCode}</p> : null}
        </div>
      );
    case 'review-seat':
      return <p className="px-4 py-3 text-sm text-muted">{panel.body}</p>;
    case 'callouts':
      return panel.callouts.length === 0 ? (
        <p className="px-4 py-3 text-sm text-muted">No shift-level judgement calls right now.</p>
      ) : (
        <ul className={cn(STAGE_ROWS, 'text-sm')}>
          {panel.callouts.map((callout) => (
            <li key={callout.title} className={TAB_ROW}>
              <p className={STAGE_ROW_TITLE}>{callout.title}</p>
              <p className="mt-0.5 text-ink-secondary">{callout.body}</p>
            </li>
          ))}
        </ul>
      );
    case 'sign-off':
      return (
        <ul className={cn(STAGE_ROWS, 'text-sm')} data-role-sign-off>
          {panel.rows.map((row) => (
            <li key={row.reviewId}>
              <ManagerSignOffRow
                gateInMeta
                item={{
                  caseId: row.caseId ?? row.reviewId,
                  title: row.summary,
                  subtitle: row.caseTitle,
                  reviewId: row.reviewId,
                  statusChips: [],
                }}
                caseItem={row.caseId ? caseById(cases, row.caseId) : undefined}
                canApproveFmea={canApproveFmea}
                onOpenCase={onOpenCase}
                onApproveReview={onApproveReview}
              />
            </li>
          ))}
        </ul>
      );
    case 'learning-trend': {
      const zeroTouchChart = roleScreenZeroTouchChart(panel);
      return (
        <div className="px-4 py-3" data-role-zero-touch-chart data-role-tab-chart="zero-touch">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted">{panel.caption}</p>
            <ProvenanceChip text="[syn]" title={panel.provenance} compact />
          </div>
          <TrendChart categories={zeroTouchChart.categories} series={zeroTouchChart.series} height={160} />
        </div>
      );
    }
    case 'recently-closed':
      return panel.caseIds.length === 0 ? (
        <p className="px-4 py-3 text-sm text-muted">No recently closed cases in this scope.</p>
      ) : (
        <div className={STAGE_ROWS}>
          {panel.caseIds.map((caseId) => {
            const caseItem = caseById(cases, caseId);
            if (!caseItem) return null;
            return (
              <FindingCard
                key={caseId}
                compact
                caseItem={caseItem}
                detailOverride={recentlyClosedCaption(caseItem)}
                actions={allActionsForCase(actions, caseId)}
                onOpen={() => onOpenCase(caseId)}
              />
            );
          })}
        </div>
      );
    default:
      return null;
  }
}

function RankedExcursionsBody({
  panel,
}: {
  panel: Extract<RoleScreenSecondaryPanel, { kind: 'ranked-excursions' }>;
}) {
  const [expanded, setExpanded] = useState(false);
  const rows = expanded ? [...panel.shown, ...panel.more] : panel.shown;
  return (
    <div>
      <ul className={cn(STAGE_ROWS, 'text-sm')}>
        {rows.map((row) => (
          <li key={row.excursionId} className={TAB_ROW}>
            <p className={STAGE_ROW_TITLE}>{row.label}</p>
            {row.chips.length > 0 ? (
              <div className="mt-1 flex flex-wrap gap-1">
                {row.chips.map((chip) => (
                  <Pill key={chip} tone="default">
                    {chip}
                  </Pill>
                ))}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {panel.moreCount > 0 ? (
        <div className="border-t border-[var(--hairline)] px-4 py-2">
          <button type="button" onClick={() => setExpanded((open) => !open)} className={STAGE_INK_LINK}>
            {expanded ? 'Show fewer' : `${panel.moreCount} more →`}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Working list rows
// ---------------------------------------------------------------------------

function ManagerSignOffRow({
  item,
  caseItem,
  canApproveFmea,
  onOpenCase,
  onApproveReview,
  gateInMeta = false,
}: {
  item: RoleScreenPrimaryItem;
  caseItem: Case | undefined;
  canApproveFmea: boolean;
  onOpenCase: (caseId: string) => void;
  onApproveReview?: (reviewId: string) => void;
  /** The card names the final gate once in its head, so the row does not repeat it. */
  gateInMeta?: boolean;
}) {
  const mayApprove = Boolean(item.reviewId && canApproveFmea && onApproveReview);
  const body = (
    <>
      <span className="min-w-0 flex-1">
        <span className={cn(STAGE_ROW_TITLE, 'block break-words')} data-role-row-title>{item.title}</span>
        {item.subtitle ? <span className="mt-0.5 block truncate text-xs text-ink-secondary">{item.subtitle}</span> : null}
        {item.detailLines?.[0] ? (
          <span className="mt-0.5 block line-clamp-1 text-xs text-muted">{item.detailLines[0].text}</span>
        ) : null}
      </span>
      {item.statusChips.length > 0 ? (
        <span className="flex max-w-[45%] shrink-0 flex-wrap justify-end gap-1">
          {item.statusChips.map((chip) => (
            <Pill key={chip.label} tone={chip.tone} className={statePillClass(chip.tone)}>
              {chip.label}
            </Pill>
          ))}
        </span>
      ) : null}
    </>
  );
  return (
    <div
      className={cn('flex items-center gap-3', mayApprove && 'pr-4')}
      data-role-row={item.caseId}
      data-role-thread-member={item.indent ? item.threadGroupId ?? '' : undefined}
    >
      {caseItem ? (
        <button
          type="button"
          onClick={() => onOpenCase(caseItem.id)}
          className={cn(STAGE_ROW_BUTTON, 'flex min-w-0 flex-1 items-center gap-3 py-2', item.indent ? 'pl-7' : 'pl-4', mayApprove ? 'pr-1' : 'pr-4')}
        >
          {body}
          <ChevronRight className="size-4 shrink-0 text-muted" aria-hidden />
        </button>
      ) : (
        <div className={cn('flex min-w-0 flex-1 items-center gap-3 py-2', item.indent ? 'pl-7' : 'pl-4', !mayApprove && 'pr-4')}>{body}</div>
      )}
      {mayApprove ? (
        <div className="flex shrink-0 items-center gap-2">
          {gateInMeta ? null : <span className="text-xs text-muted">Final gate: {APPROVAL_GATE_SEAT}</span>}
          <button
            type="button"
            className={cn(GATE_PRIMARY_BUTTON, 'h-7 px-3')}
            onClick={() => onApproveReview!(item.reviewId!)}
          >
            Approve
          </button>
        </div>
      ) : null}
    </div>
  );
}

function PrimaryListItem({
  item,
  screenId,
  cases,
  actions,
  canApproveFmea,
  onOpenCase,
  onApproveReview,
  showGroupHeader = false,
  cursor,
}: {
  item: RoleScreenPrimaryItem;
  screenId: RoleScreenViewModel['screenId'];
  cases: readonly Case[];
  actions: readonly RecommendedAction[];
  canApproveFmea: boolean;
  onOpenCase: (caseId: string) => void;
  onApproveReview?: (reviewId: string) => void;
  showGroupHeader?: boolean;
  cursor: SeriesTime;
}) {
  const caseItem = caseById(cases, item.caseId);

  if (item.threadHead) {
    // A thread head is a caps subhead band inside the list (comp `.thd`).
    return (
      <div className="flex flex-wrap items-center gap-2 bg-[var(--surface-1)] px-4 py-1.5" data-role-thread-head>
        <p className={STAGE_LABEL_CAPS} data-role-thread-label>
          {item.threadHead.rootLabel} · {item.threadHead.signalCount} signals
          {screenId !== 'manager' ? ` · ${item.threadHead.yoursCount} yours` : ''}
        </p>
        {item.statusChips.map((chip) => (
          <Pill key={chip.label} tone={chip.tone} title={chip.title} className={statePillClass(chip.tone)}>
            {chip.label}
          </Pill>
        ))}
      </div>
    );
  }

  if (item.quiet) {
    return (
      <button
        type="button"
        onClick={() => onOpenCase(item.caseId)}
        className={cn(STAGE_ROW_BUTTON, 'block px-4')}
        data-role-row={item.caseId}
        data-role-thread-member={item.indent ? item.threadGroupId ?? '' : undefined}
      >
        <div className="flex items-center justify-between gap-2 py-1.5 pl-3 text-sm">
          <div className="min-w-0 flex-1">
            <p className="break-words text-ink-secondary" data-role-row-title>{item.title}</p>
            {item.subtitle ? (
              <p className="truncate font-mono text-[10px] text-muted">{item.subtitle}</p>
            ) : null}
          </div>
          <div className="flex max-w-[45%] shrink-0 flex-wrap justify-end gap-1">
            {item.statusChips.map((chip) => (
              <Pill key={chip.label} tone={chip.tone} title={chip.title} className={statePillClass(chip.tone)}>
                {chip.label}
              </Pill>
            ))}
          </div>
          <ChevronRight className="size-4 shrink-0 text-muted" aria-hidden />
        </div>
      </button>
    );
  }

  if (screenId === 'manager') {
    return (
      <ManagerSignOffRow
        item={item}
        caseItem={caseItem}
        canApproveFmea={canApproveFmea}
        onOpenCase={onOpenCase}
        onApproveReview={onApproveReview}
      />
    );
  }

  if (screenId === 'super' && item.ageLabel && item.resourcingAsk) {
    return (
      <button
        type="button"
        onClick={() => onOpenCase(item.caseId)}
        className={cn(STAGE_ROW_BUTTON, 'grid grid-cols-[1fr_auto_auto_auto] items-start gap-2 px-4 py-2 text-sm')}
      >
        <div className="min-w-0">
          <p className={cn(STAGE_ROW_TITLE, 'break-words')} data-role-row-title>{item.title}</p>
          {item.subtitle ? <p className={STAGE_ROW_META}>{item.subtitle}</p> : null}
        </div>
        <span className="shrink-0 text-xs tabular-nums text-ink-secondary">{item.ageLabel}</span>
        <span className="shrink-0 text-xs text-ink-secondary">{item.shiftRaised}</span>
        <span className="shrink-0 text-xs font-medium text-ink-primary">{item.resourcingAsk}</span>
      </button>
    );
  }

  if (!caseItem) return null;

  return (
    <div data-role-row={item.caseId} data-role-thread-member={item.indent ? item.threadGroupId ?? '' : undefined}>
      {showGroupHeader && item.group ? (
        <p className={cn(STAGE_LABEL_CAPS, 'px-4 pb-1 pt-3')}>{item.group}</p>
      ) : null}
      <FindingCard
        compact
        className={item.indent ? 'pl-7' : undefined}
        caseItem={caseItem}
        actions={allActionsForCase(actions, caseItem.id)}
        correlationLine={item.correlationLine}
        statusChips={item.statusChips}
        cursor={cursor}
        onOpen={() => onOpenCase(caseItem.id)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------

export function RoleScreen({
  model,
  cases,
  actions,
  projectPath,
  sessionState,
  activeScenarios = [],
  onToggleScenario,
  showScenarioToggle = true,
  showScopeMeta = true,
  canApproveFmea,
  onOpenCase,
  onApproveReview,
}: RoleScreenProps) {
  const [showAllPrimary, setShowAllPrimary] = useState(false);
  const tabs = roleScreenTabs(model);
  // The tab resets when the persona picks another screen.
  const [tabState, setTabState] = useState<{ screen: string; id: string } | null>(null);
  const activeTabId = tabState?.screen === model.screenId && tabs.some((tab) => tab.id === tabState.id)
    ? tabState.id
    : tabs[0]?.id;
  const activeTab = tabs.find((tab) => tab.id === activeTabId);
  const mediaDepletionOn = activeScenarios.includes('media-depletion');
  const mediaDepletionBundle = SCENARIOS.find((s) => s.id === 'media-depletion');
  const clearScopeHref = model.scopeId ? `${projectPath}/advisory` : undefined;
  const primaryUnitCount = primaryListUnits(model.primary.items).length;
  const primaryVisibleItems = showAllPrimary
    ? model.primary.items
    : visiblePrimaryListItems(model.primary.items, PRIMARY_LIST_CAP);
  const totalRowCount = model.primary.items.filter((item) => !item.threadHead).length;
  // The gate is named once, in the card head, where the screen holds one.
  const headMeta = model.screenId === 'manager' && canApproveFmea && onApproveReview ? (
    <span>Final gate: <span className="font-semibold text-ink-secondary">{APPROVAL_GATE_SEAT}</span></span>
  ) : showScopeMeta ? (
    <span>
      {model.scopeLine}
      {clearScopeHref ? (
        <>
          {' · '}
          <Link to={clearScopeHref} className={STAGE_INK_LINK}>clear</Link>
        </>
      ) : null}
    </span>
  ) : undefined;

  return (
    <Commentable name="RoleScreen">
      <div data-role-screen={model.screenId}>
        <SurfacePagePanel
          title={model.title}
          subtitle={model.screenId === 'cro' && model.question ? model.question : undefined}
          meta={headMeta}
          actions={
            <span className="flex items-center gap-2">
              {showScenarioToggle && onToggleScenario ? (
                <button
                  type="button"
                  aria-pressed={mediaDepletionOn}
                  title={mediaDepletionBundle?.description}
                  onClick={() => onToggleScenario('media-depletion')}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    mediaDepletionOn
                      ? 'border-transparent bg-ink-primary text-[var(--surface-2)]'
                      : 'border-[var(--hairline-strong)] bg-[var(--surface-2)] text-ink-secondary hover:text-ink-primary',
                  )}
                >
                  Media depletion
                </button>
              ) : null}
              <HelpAnchor doc="advisory/role-screens" label="Role screens" />
            </span>
          }
          flush
          contentClassName="pb-0"
        >
          <Commentable name="Kpis">
            <div
              data-role-facts
              className="grid grid-cols-2 divide-x divide-[var(--hairline)] border-b border-[var(--hairline)] lg:grid-cols-4"
            >
              {model.kpis.map((kpi) => (
                <FactCell key={kpi.label} kpi={kpi} onOpenCase={onOpenCase} />
              ))}
            </div>
          </Commentable>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
            <Commentable name="Primary">
              <div className="min-w-0 border-[var(--hairline)] max-lg:border-b lg:border-r">
                <div className={PRIMARY_SCROLL} data-role-primary-scroll>
                  <p className={cn(STAGE_LABEL_CAPS, 'sticky top-0 z-10 border-b border-[var(--hairline)] bg-[var(--surface-2)] px-4 py-2')}>
                    {model.primary.title}
                  </p>
                  {model.screenId === 'cro' && model.primary.forecastLine ? (
                    <p className="border-b border-divider px-4 py-2.5 text-sm text-ink-primary">
                      {model.primary.forecastLine}
                    </p>
                  ) : null}
                  {model.primary.items.length === 0 ? (
                    <div className="px-4 py-3">
                      <EmptyState
                        embedded
                        icon={<Inbox className="h-8 w-8 text-muted" />}
                        title={model.primary.emptyMessage}
                      />
                    </div>
                  ) : (
                    <div className={STAGE_ROWS} data-role-primary-list>
                      {model.screenId === 'super' ? (
                        <div className={cn(STAGE_LABEL_CAPS, 'grid grid-cols-[1fr_auto_auto_auto] gap-2 px-4 py-2')}>
                          <span>Case</span>
                          <span>Age</span>
                          <span>Shift</span>
                          <span>Resourcing ask</span>
                        </div>
                      ) : null}
                      {primaryVisibleItems.map((item, index) => {
                        const showGroupHeader = Boolean(
                          item.group &&
                            (index === 0 || primaryVisibleItems[index - 1]?.group !== item.group),
                        );
                        return (
                          <PrimaryListItem
                            key={`${item.caseId}-${item.reviewId ?? ''}`}
                            item={item}
                            screenId={model.screenId}
                            cases={cases}
                            actions={actions}
                            canApproveFmea={canApproveFmea}
                            onOpenCase={onOpenCase}
                            onApproveReview={onApproveReview}
                            showGroupHeader={showGroupHeader}
                            cursor={sessionState.cursor}
                          />
                        );
                      })}
                      {primaryUnitCount > PRIMARY_LIST_CAP ? (
                        <button
                          type="button"
                          onClick={() => setShowAllPrimary((open) => !open)}
                          className={cn(STAGE_ROW_BUTTON, 'px-4 py-2 text-xs font-medium text-ink-secondary underline decoration-[var(--hairline-strong)] underline-offset-2 hover:text-ink-primary')}
                        >
                          {showAllPrimary ? 'Show fewer cases' : `Show all ${totalRowCount} cases`}
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </Commentable>

            <Commentable name="Secondary">
              <div className="min-w-0">
                {tabs.length > 0 && activeTab ? (
                  <>
                    <div className="border-b border-[var(--hairline)] px-3 py-2">
                      <SurfacePageTabs
                        aria-label={`${model.title} panels`}
                        variant="ink"
                        density="compact"
                        items={tabs.map((tab) => ({ id: tab.id, label: tab.label, count: tab.headline || undefined }))}
                        value={activeTab.id}
                        onChange={(id) => setTabState({ screen: model.screenId, id })}
                      />
                    </div>
                    <div className={TAB_SCROLL} data-role-tab-panel={activeTab.panel.kind}>
                      <TabBody
                        panel={activeTab.panel}
                        cases={cases}
                        actions={actions}
                        sessionState={sessionState}
                        onOpenCase={onOpenCase}
                        canApproveFmea={canApproveFmea}
                        onApproveReview={onApproveReview}
                      />
                    </div>
                  </>
                ) : null}
              </div>
            </Commentable>
          </div>

          {model.footer ? (
            <p className="border-t border-[var(--hairline)] px-4 py-2 text-xs text-muted">{model.footer}</p>
          ) : null}
        </SurfacePagePanel>
      </div>
    </Commentable>
  );
}
